import { useDrag, useZoom } from 'hooks/user/useAuditoriumSeats'
import { Tooltip } from 'primereact/tooltip'
import React, { useCallback, useEffect, useState } from 'react'
import { Seat } from 'types/Location'
import { generateSeatLabelData } from 'utils/seatLabel'

import { Cell, Container, Grid, GridContainer, GridWrapper, TitleBox, ZoomButton, ZoomContainer } from '../auditorium.style'
import MiddleColumnSelector from './MiddleColumnSelector'
import seat from './seat.svg'

interface AuditoriumProps {
    rows: number | null
    columns: number | null
    seats: Omit<Seat, 'seat_id'>[]
    toggleSeat: (row: number, column: number, isGallery?: boolean) => void
    middleColumn?: number
    onMiddleColumnChange: (column: number) => void
    name: string
    isGallery?: boolean
}

export const Auditorium: React.FC<AuditoriumProps> = ({
    rows,
    columns,
    seats,
    toggleSeat,
    middleColumn,
    onMiddleColumnChange,
    name,
    isGallery = false,
}) => {
    const [defaultMiddleColumn, setDefaultMiddleColumn] = useState<number | undefined>(middleColumn)
    const [loading, setLoading] = useState<boolean>(true)
    const { containerRef, handleDragStart, handleDragMove, handleDragEnd } = useDrag()
    const { zoomLevel, handleZoomIn, handleZoomOut } = useZoom(columns || 0)

    useEffect(() => {
        if (columns) {
            const calculatedMiddle = Math.ceil(columns / 2)
            setDefaultMiddleColumn(calculatedMiddle)
            onMiddleColumnChange(calculatedMiddle)
        }
    }, [columns, onMiddleColumnChange])

    useEffect(() => {
        setLoading(false)
    }, [rows, columns])

    useEffect(() => {
        if (containerRef.current && !loading) {
            const container = containerRef.current
            container.scrollLeft = container.scrollWidth / 2 - container.clientWidth / 2
        }
    }, [containerRef, loading, rows, columns])

    const isSeat = useCallback((row: number, column: number) => seats?.some((seat) => seat.row === row && seat.column === column), [seats])

    if (!rows || !columns) return null

    return (
        <Container>
            <TitleBox>Színpad</TitleBox>
            <GridContainer
                ref={containerRef}
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
            >
                <GridWrapper style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center', transition: 'transform 0.3s ease' }}>
                    <MiddleColumnSelector
                        columns={columns}
                        defaultMiddleColumn={defaultMiddleColumn}
                        setDefaultMiddleColumn={setDefaultMiddleColumn}
                        onMiddleColumnChange={onMiddleColumnChange}
                        name={name}
                    />

                    <Grid rows={rows + 2} columns={columns}>
                        {Array.from({ length: rows }).map((_, rowIndex) => (
                            <React.Fragment key={rowIndex}>
                                {Array.from({ length: columns }).map((_, columnIndex) => (
                                    <div key={columnIndex} style={{ display: 'flex' }}>
                                        <Cell
                                            id={`seat-${rowIndex}-${columnIndex}-${name}`}
                                            onClick={() => toggleSeat(rowIndex, columnIndex, isGallery)}
                                            highlight={defaultMiddleColumn === columnIndex}
                                        >
                                            {isSeat(rowIndex, columnIndex) && <img src={seat} alt="Seat" />}
                                        </Cell>
                                        <Tooltip
                                            target={`#seat-${rowIndex}-${columnIndex}-${name}`}
                                            content={
                                                generateSeatLabelData(rowIndex, columnIndex, columns, seats, middleColumn, isGallery)?.label
                                            }
                                            position="right"
                                            showOnDisabled
                                            showDelay={400}
                                            hideDelay={200}
                                        />
                                    </div>
                                ))}
                            </React.Fragment>
                        ))}
                        <React.Fragment></React.Fragment>
                    </Grid>
                </GridWrapper>
            </GridContainer>
            <ZoomContainer>
                <ZoomButton type="button" onClick={handleZoomIn} icon="pi pi-search-plus" />
                <ZoomButton type="button" onClick={handleZoomOut} icon="pi pi-search-minus" />
            </ZoomContainer>
            <TitleBox>{`${seats.length} db férőhely`}</TitleBox>
        </Container>
    )
}
